import { useState, useCallback, useContext, useEffect, useRef, useMemo, forwardRef } from 'react';
import type { FormEvent, Ref, ChangeEvent, PropsWithoutRef } from 'react';

import {
  Button,
  FileInput,
  FileVisual,
  Progress,
  Modal,
  Text,
  SummaryList,
  ViewAll,
  getKindFromMimeType,
  getMimeTypeFromFile,
  Flex,
  Input,
  ComboBox,
  createUID,
  useModalManager,
  useModalContext,
  useToaster,
  DateTimeDisplay,
  MetaList,
  FormDialog
} from '@pega/cosmos-react-core';
import type { Action, SummaryListItem, FormDialogProps } from '@pega/cosmos-react-core';

import { loadingTimeoutMS } from '../../core/Progress/Progress.mocks';
import {
  FileServiceContext,
  mockAttachmentListRequest,
  mockCategories
} from '../../core/File/FileService.mock';
import type {
  FileServiceContextValue,
  MockFileAttachmentResource,
  MockFileUploadItem,
  MockFileItem
} from '../../core/File/FileService.mock';

// ---------------------------------------------------------------------------------------------------------------------
// getSummaryListItemProps
// ---------------------------------------------------------------------------------------------------------------------
const getSummaryListItemProps = ({
  ref,
  file,
  editFile,
  cancelFile,
  deleteFile
}: {
  ref: Ref<HTMLLIElement>;
  file: MockFileAttachmentResource | MockFileUploadItem;
  editFile?: (file: MockFileAttachmentResource) => void;
  cancelFile: FileServiceContextValue['cancelFile'];
  deleteFile: FileServiceContextValue['deleteFile'];
}): SummaryListItem => {
  let actions: Action[];
  let visual: JSX.Element;

  if ('progress' in file && typeof file.progress === 'number' && file.progress < 100) {
    visual = <Progress value={file.progress} placement='inline' variant='ring' />;
    actions = [
      {
        id: 'Cancel',
        text: 'Cancel',
        icon: 'times',
        onClick: () => {
          cancelFile(file.id);
        }
      }
    ];
  } else {
    visual = (
      <FileVisual
        type={getKindFromMimeType(getMimeTypeFromFile(file.name) ?? '')}
        thumbnail={(file as MockFileAttachmentResource).thumbnail}
      />
    );
    actions = [
      {
        id: 'Edit',
        text: 'Edit',
        icon: 'pencil',
        onClick: () => {
          editFile?.(file);
        }
      },
      {
        id: 'Download',
        text: 'Download',
        icon: 'download',
        onClick: () => {}
      },
      {
        id: 'Delete',
        text: 'Delete',
        icon: 'trash',
        onClick: () => {
          deleteFile(file.id);
        }
      }
    ];
  }

  return {
    ref,
    id: file.id,
    visual,
    primary: file.name,
    contextualLabel: file.name,
    secondary: (
      <Text variant='secondary'>
        {'progress' in file && typeof file.progress === 'number' && file.progress < 100 ? (
          `Uploading…${Math.floor(file.progress)}%`
        ) : (
          <MetaList
            items={[
              file.category,
              <>
                Uploaded{' '}
                <DateTimeDisplay
                  variant='relative'
                  value={(file as MockFileAttachmentResource).createDate}
                />
              </>
            ]}
          />
        )}
      </Text>
    ),
    actions
  };
};

// ---------------------------------------------------------------------------------------------------------------------
// Attachment edit Dialog
// ---------------------------------------------------------------------------------------------------------------------
interface EditDialogProps {
  file: MockFileAttachmentResource;
  target: FormDialogProps['target'];
  onSubmit: (fields: { name: string; category: string }) => void;
  onCancel: () => void;
  ref: Ref<HTMLDivElement>;
}

const EditDialog = forwardRef(function EditDialog(
  { file, target, onSubmit, onCancel }: PropsWithoutRef<EditDialogProps>,
  ref: EditDialogProps['ref']
) {
  const [name, setName] = useState(file.name);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [categories, setCategories] = useState(() => {
    return [
      { id: createUID(), primary: 'No category', selected: false },
      ...mockCategories.map(category => {
        return {
          id: createUID(),
          primary: category,
          selected: file.category === category
        };
      })
    ];
  });

  const selectedCategory = useMemo(() => {
    const sc = categories.find(c => c.selected);
    return sc
      ? {
          items: {
            id: sc.id,
            text: sc.primary
          }
        }
      : undefined;
  }, [categories]);

  return (
    <FormDialog
      ref={ref}
      heading='Edit attachment'
      target={target}
      progress={loading ? 'Updating attachment...' : undefined}
      placement='bottom-start'
      onCancel={onCancel}
      onSubmit={() => {
        if (error) return;
        setLoading(true);
        setTimeout(() => {
          onSubmit({
            name,
            category:
              !selectedCategory || selectedCategory.items.text === 'No category'
                ? ''
                : selectedCategory.items.text
          });
          setLoading(false);
        }, loadingTimeoutMS);
      }}
    >
      <Flex container={{ direction: 'column', gap: 2 }}>
        <Input
          label='Name'
          placeholder='Enter name…'
          value={name}
          info={error}
          status={error ? 'error' : undefined}
          autoFocus
          required
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setName(e.target.value);
            if (error && e.target.value) {
              setError('');
            }
          }}
          onBlur={() => {
            if (!name) {
              setError('Name is required.');
            }
          }}
        />
        <ComboBox
          label='Category'
          placeholder='Choose…'
          selected={selectedCategory}
          menu={{
            mode: 'single-select',
            items: categories,
            onItemClick: id => {
              setCategories(cur => {
                return cur.map(category => {
                  return {
                    ...category,
                    selected: category.id === id
                  };
                });
              });
            }
          }}
        />
      </Flex>
    </FormDialog>
  );
});

// ---------------------------------------------------------------------------------------------------------------------
// AttachmentsModal
// ---------------------------------------------------------------------------------------------------------------------
interface AttachmentsModalProps {
  defaultMode: 'viewAll' | 'addNew';
}

const AttachmentsModal = ({ defaultMode }: AttachmentsModalProps) => {
  const [mode, setMode] = useState(defaultMode);

  const { push: pushToaster } = useToaster();
  const { dismiss } = useModalContext();
  const { files, setFiles, cancelFile, attachFile, deleteFile } = useContext(FileServiceContext);

  const [search, setSearch] = useState('');
  const [viewAllLoading, setViewAllLoading] = useState(true);
  const [addNewLoading, setAddNewLoading] = useState(false);
  const [newFiles, setNewFiles] = useState<MockFileItem[]>([]);

  const attachSelected = useCallback(() => {
    newFiles.forEach(attachFile);
    dismiss();
  }, [newFiles, attachFile]);

  const listItemEls = useRef<HTMLLIElement[]>([]);
  const [editFileIndex, setEditFileIndex] = useState<number>(-1);

  const editDialogTargetLi = useMemo(() => {
    return editFileIndex !== undefined ? listItemEls.current[editFileIndex] : undefined;
  }, [editFileIndex, listItemEls]);

  const menuButton = useMemo(
    () =>
      editDialogTargetLi
        ? editDialogTargetLi.querySelector<HTMLButtonElement>('button[aria-haspopup="menu"]')
        : null,
    [editDialogTargetLi]
  );

  const handleDialogClose = useCallback(() => {
    if (!editDialogTargetLi) return;
    setEditFileIndex(-1);
    menuButton?.focus();
  }, [menuButton, editDialogTargetLi]);

  const handleKeydown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      setEditFileIndex(-1);
    }
  }, []);

  const editDialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (mode === 'viewAll') {
      const timeout = setTimeout(() => {
        setViewAllLoading(false);
      }, 1000);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [mode]);

  useEffect(() => {
    if (!editDialogTargetLi) return;
    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [editDialogTargetLi]);

  useEffect(() => {
    // Since the component is performing double duty we need to empty out ref elements from a prior render mode.
    listItemEls.current = [];
  }, [mode]);

  const onFilesAdded = useCallback(
    (newlyAddedFiles: File[]) => {
      setNewFiles(current => [
        ...newlyAddedFiles.map((file, i) => {
          const id = createUID();
          return {
            id,
            name: file.name,
            format: file.name.split('.').pop(),
            category: '',
            File: file,
            onEdit: () => {
              setEditFileIndex(i);
            },
            onCancel: () => {
              setNewFiles(c => c.filter(f => f.id !== id));
            }
          };
        }),
        ...current
      ]);
    },
    [files]
  );

  // Actions for the modal
  const actions =
    mode === 'addNew' ? (
      <>
        <Button onClick={dismiss} disabled={addNewLoading}>
          Cancel
        </Button>
        <Button
          type='submit'
          variant='primary'
          // Normally this would trigger a submit event but we are preventing that in the Modal's onSubmit.
          onClick={() => {
            setAddNewLoading(true);
            setTimeout(() => {
              attachSelected();
              setAddNewLoading(false);
            }, loadingTimeoutMS);
          }}
          disabled={newFiles.length === 0 || addNewLoading}
        >
          Upload files
        </Button>
      </>
    ) : null;

  // Content for the modal
  const content =
    mode === 'addNew' ? (
      <FileInput
        multiple
        label='Select files to upload'
        onFilesAdded={onFilesAdded}
        files={newFiles.map(f => ({ ...f, meta: f.category || undefined }))}
      />
    ) : (
      <ViewAll
        loading={viewAllLoading}
        items={(search ? files.filter(f => f.name.includes(search)) : files).map((file, i) => {
          return getSummaryListItemProps({
            ref: (el: HTMLLIElement) => {
              listItemEls.current[i] = el;
            },
            file,
            editFile: () => {
              setEditFileIndex(i);
            },
            cancelFile: id => {
              const canceledFile = files.find(f => f.id === id) as MockFileUploadItem;
              cancelFile(id);
              pushToaster({ content: `Cancelled uploading ${canceledFile.name}` });
            },
            deleteFile: async id => {
              const deletedFile = files.find(f => f.id === id) as MockFileAttachmentResource;
              await deleteFile(id);
              pushToaster({ content: `${deletedFile.name} deleted` });
            }
          });
        })}
        actions={[
          {
            id: 'addNewFile',
            text: 'Add files',
            onClick: () => {
              setMode('addNew');
            }
          },
          {
            id: 'addNewLink',
            text: 'Add links',
            onClick: () => {
              setMode('addNew');
            }
          }
        ]}
        searchInputProps={{ onSearchChange: value => setSearch(value) }}
      />
    );

  return (
    <Modal
      as={mode === 'addNew' ? 'form' : undefined}
      heading={mode === 'addNew' ? 'Add attachments' : 'Attachments'}
      actions={actions}
      progress={addNewLoading ? { message: 'Adding attachments...' } : undefined}
      onRequestDismiss={() => {
        return !editDialogTargetLi;
      }}
      onSubmit={
        mode === 'addNew'
          ? (e: FormEvent) => {
              e.preventDefault();
            }
          : undefined
      }
    >
      <>
        {content}
        {editDialogTargetLi && menuButton && (
          <EditDialog
            ref={editDialogRef}
            target={menuButton}
            file={mode === 'addNew' ? newFiles[editFileIndex] : files[editFileIndex]}
            onCancel={handleDialogClose}
            onSubmit={({ name, category }) => {
              handleDialogClose();

              // TS kept from easily consolidating like:
              // const setter = mode === 'addNew' ? setNewFiles : setFiles;
              // Could not avoid map's file param as "any".
              if (mode === 'addNew') {
                setNewFiles(cur => {
                  return cur.map((file, i) => {
                    if (i === editFileIndex) {
                      return {
                        ...file,
                        name,
                        category
                      };
                    }

                    return file;
                  });
                });
              } else {
                setFiles(cur =>
                  cur.map((file, i) => {
                    if (i === editFileIndex) {
                      return {
                        ...file,
                        name,
                        category
                      };
                    }

                    return file;
                  })
                );
              }
            }}
          />
        )}
      </>
    </Modal>
  );
};

// ---------------------------------------------------------------------------------------------------------------------
// Attachments mock
// ---------------------------------------------------------------------------------------------------------------------
export const AttachmentsMock = () => {
  const { push: pushToaster } = useToaster();
  const { create: createModal } = useModalManager();
  const [loading, setLoading] = useState(true);
  const { files, setFiles, cancelFile, deleteFile } = useContext(FileServiceContext);

  const listItemEls = useRef<HTMLLIElement[]>([]);
  const [editFileIndex, setEditFileIndex] = useState<number>(-1);

  const editDialogTargetLi = useMemo(() => {
    return editFileIndex !== undefined ? listItemEls.current[editFileIndex] : undefined;
  }, [editFileIndex, listItemEls]);

  const menuButton = useMemo(
    () =>
      editDialogTargetLi
        ? editDialogTargetLi.querySelector<HTMLButtonElement>('button[aria-haspopup="menu"]')
        : null,
    [editDialogTargetLi]
  );

  const handleDialogClose = useCallback(() => {
    if (!editDialogTargetLi) return;
    setEditFileIndex(-1);
    menuButton?.focus();
  }, [menuButton, editDialogTargetLi]);

  const handleKeydown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      setEditFileIndex(-1);
    }
  }, []);

  const editDialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let mounted = true;

    (async () => {
      const attachments = await mockAttachmentListRequest();

      // Avoids: Warning: Can't perform a React state update on an unmounted component.
      if (mounted) {
        setLoading(false);
        setFiles(attachments);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!editDialogTargetLi) return;
    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [editDialogTargetLi]);

  return (
    <>
      <SummaryList
        icon='paper-clip'
        name='Attachments'
        headingTag='h3'
        loading={loading}
        count={!loading && Array.isArray(files) ? files.length : undefined}
        actions={[
          {
            text: 'Add',
            id: 'addNew',
            icon: 'plus',
            onClick() {
              createModal(AttachmentsModal, { defaultMode: 'addNew' as const });
            }
          }
        ]}
        items={files.slice(0, 3).map((file, i) => {
          return getSummaryListItemProps({
            ref: (el: HTMLLIElement) => {
              listItemEls.current[i] = el;
            },
            file,
            editFile: () => {
              setEditFileIndex(i);
            },
            cancelFile: id => {
              const canceledFile = files.find(f => f.id === id) as MockFileUploadItem;
              cancelFile(id);
              pushToaster({ content: `Cancelled uploading ${canceledFile.name}` });
            },
            deleteFile: async id => {
              const deletedFile = files.find(f => f.id === id) as MockFileAttachmentResource;
              await deleteFile(id);
              pushToaster({ content: `${deletedFile.name} deleted` });
            }
          });
        })}
        onViewAll={() => {
          createModal(AttachmentsModal, { defaultMode: 'viewAll' as const });
        }}
      />
      {editDialogTargetLi && menuButton && (
        <EditDialog
          ref={editDialogRef}
          target={menuButton}
          file={files[editFileIndex]}
          onCancel={handleDialogClose}
          onSubmit={({ name, category }) => {
            handleDialogClose();
            setFiles(cur =>
              cur.map((file, i) => {
                if (i === editFileIndex) {
                  return {
                    ...file,
                    name,
                    category
                  };
                }

                return file;
              })
            );
          }}
        />
      )}
    </>
  );
};
